/**
 * QuoteSubmitPage — /quotes/submit
 * Any signed-in user can submit a new quote (any language). The
 * backend dedupes against existing quotes and pending submissions,
 * and emails the submitter when a decision is made.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, ArrowLeft, Quote as QuoteIcon } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

const MAX = 300;

const CATEGORIES = [
  "philosophy","courage","success","science","literature","leadership",
  "kindness","character","wit","time","purpose","inspiration",
  "family","community","business","education","health","travel",
  "memorial","heritage","india","faith","environment",
];

const LANGS = [
  { key: "en", label: "English" },
  { key: "hi", label: "हिन्दी (Hindi)" },
  { key: "ml", label: "മലയാളം (Malayalam)" },
  { key: "ta", label: "தமிழ் (Tamil)" },
  { key: "es", label: "Español" },
  { key: "fr", label: "Français" },
  { key: "de", label: "Deutsch" },
  { key: "pt", label: "Português" },
  { key: "ar", label: "العربية" },
  { key: "zh", label: "中文" },
];

export default function QuoteSubmitPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    text: "", author_name: "", category: "inspiration",
    language: "en", country: "", source_url: "", notes: "",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!token) { toast.error("Sign in first."); return; }
    if (form.text.length > MAX) { toast.error(`Quote too long (max ${MAX} chars).`); return; }
    setSaving(true);
    try {
      const r = await axios.post(
        `${API}/api/quotes/submit`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (r.data.duplicate) {
        toast.warning(r.data.message || "Duplicate detected.");
        if (r.data.existing) navigate(r.data.existing.url);
        return;
      }
      toast.success("Submitted for review — we'll email you when a decision is made.");
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submit failed");
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return (
      <div className="max-w-xl mx-auto py-16 px-4 text-center">
        <QuoteIcon className="w-10 h-10 mx-auto opacity-40 mb-3" />
        <h1 className="font-display text-3xl mb-2">Sign in to submit a quote</h1>
        <p className="opacity-70 mb-4">Anyone signed in can suggest a quote in any language — we'll review and email you the decision.</p>
        <Link to="/auth" className="wofy-btn-primary inline-block">Sign in</Link>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto py-8 px-4" data-testid="quote-submit">
      <Link to="/" className="inline-flex items-center gap-1 text-sm opacity-70 hover:opacity-100 mb-4">
        <ArrowLeft className="w-4 h-4" /> Home
      </Link>
      <h1 className="font-display text-3xl mb-1">Submit a Quote</h1>
      <p className="text-sm opacity-70 mb-6">
        Share an inspirational quote for the Social Pulse Wisdom Center.
        Duplicates get flagged automatically. We support quotes in every language —
        please add a language tag so we can group them properly.
      </p>

      <form onSubmit={submit} className="wofy-card !p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">Quote text * <span className="opacity-60 text-xs">({form.text.length}/{MAX})</span></label>
          <textarea
            required
            rows={4}
            value={form.text}
            onChange={set("text")}
            maxLength={MAX}
            className="w-full px-3 py-2 rounded-lg border"
            style={{ borderColor: "var(--wofy-border)" }}
            placeholder="“The greatest joy in life comes from helping others.”"
            data-testid="submit-text"
          />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium mb-1">Author *</label>
            <input required value={form.author_name} onChange={set("author_name")} maxLength={120}
              className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wofy-border)" }} data-testid="submit-author" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Category</label>
            <select value={form.category} onChange={set("category")} className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wofy-border)" }} data-testid="submit-category">
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium mb-1">Language</label>
            <select value={form.language} onChange={set("language")} className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wofy-border)" }} data-testid="submit-language">
              {LANGS.map(l => <option key={l.key} value={l.key}>{l.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Country (optional)</label>
            <input value={form.country} onChange={set("country")} className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wofy-border)" }} placeholder="India / United States / …" data-testid="submit-country" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Source URL (optional)</label>
          <input value={form.source_url} onChange={set("source_url")} maxLength={400}
            className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wofy-border)" }}
            placeholder="https://en.wikiquote.org/wiki/…" data-testid="submit-source" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Notes for our editors (optional)</label>
          <textarea rows={2} value={form.notes} onChange={set("notes")} maxLength={800}
            className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wofy-border)" }}
            data-testid="submit-notes" />
        </div>
        <div className="flex items-center justify-between pt-2">
          <p className="text-xs opacity-60">We'll email you the review decision.</p>
          <button type="submit" disabled={saving || !form.text || !form.author_name}
            className="wofy-btn-primary inline-flex items-center gap-2" data-testid="submit-btn">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" />Submitting…</> : <><Send className="w-4 h-4" />Submit</>}
          </button>
        </div>
      </form>
    </div>
  );
}
